import { useCallback, useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { useSubscribe } from "@/contexts/RealtimeContext";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Siren, Info, AlertTriangle, Mail, CheckCircle2, Loader2, Phone } from "lucide-react";

export default function EmergencyPage() {
  const { propertyId } = useProperty();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ severity: "info", title: "", body: "", send_email: true, send_sms: false });
  const [sending, setSending] = useState(false);
  const [testing, setTesting] = useState(null);
  const load = useCallback(async () => {
    if (!propertyId) return;
    const { data } = await api.get(`/alerts?property_id=${propertyId}`);
    setItems(data.items);
  }, [propertyId]);
  useEffect(() => { load(); }, [load]);

  // Live: alerts fired from ANY admin console show up here + trigger OS notification.
  useSubscribe(propertyId ? `property:${propertyId}` : null, ({ event, payload }) => {
    if (event === "alert.new") setItems((prev) => [payload, ...prev]);
  });

  // Ask for browser notification permission once, so critical alerts can pop natively.
  useEffect(() => {
    if (!("Notification" in window)) return;
    if (Notification.permission === "default") {
      // Fire-and-forget; user can decline without any UI impact here.
      Notification.requestPermission().catch(() => {});
    }
  }, []);
  const testSend = async (channel) => {
    setTesting(channel);
    try {
      const { data } = await api.post("/alerts/test", { channel, property_id: propertyId });
      const r = data.result || {};
      if (r.ok) toast.success(`Test ${channel} sent to ${data.to}`);
      else toast.error(`Test ${channel} queued — ${r.error || "channel not configured"}`);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
    setTesting(null);
  };
  const submit = async () => {
    if (!form.title || !form.body) return toast.error("Title and body required");
    setSending(true);
    try {
      const { data } = await api.post(`/alerts?property_id=${propertyId}`, form);
      const er = data.email_result, sr = data.sms_result;
      let msg = "Broadcast sent";
      if (form.send_email && er) {
        msg += ` · email ${er.sent > 0 ? `${er.sent}/${er.recipients} delivered` : `${er.queued} queued`}`;
      }
      if (form.send_sms && sr) {
        msg += ` · SMS ${sr.sent > 0 ? `${sr.sent}/${sr.recipients} delivered` : `${sr.queued} queued`}`;
      }
      toast.success(msg);
      setForm({ severity: "info", title: "", body: "", send_email: true, send_sms: false });
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
    setSending(false);
  };
  const IconFor = { info: Info, warning: AlertTriangle, critical: Siren };
  const isCritical = form.severity === "critical";
  return (
    <div>
      <PageHeader eyebrow="Community" title="Emergency & Alerts" subtitle="Broadcast to every resident — post here and optionally to their email inbox." />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-1 border-white/10 bg-card p-5">
          <div className="overline mb-3">New broadcast</div>
          <div className="space-y-3">
            <div><Label>Severity</Label>
              <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
                <SelectTrigger data-testid="alert-severity"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="info">Info</SelectItem>
                  <SelectItem value="warning">Warning</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Title</Label><Input data-testid="alert-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
            <div><Label>Body</Label><Textarea data-testid="alert-body" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={4} /></div>
            <div className="flex items-center justify-between rounded-md border border-white/10 bg-white/[0.02] p-3">
              <div>
                <div className="text-sm font-medium flex items-center gap-2"><Mail className="h-4 w-4 text-indigo-400" /> Also email residents</div>
                <div className="text-[11px] text-zinc-500">Sends a formatted email to every resident of this property.</div>
              </div>
              <Switch checked={form.send_email} onCheckedChange={(v) => setForm({ ...form, send_email: v })} data-testid="alert-email-toggle" />
            </div>
            <div className={"flex items-center justify-between rounded-md border p-3 " + (isCritical ? "border-rose-500/40 bg-rose-500/5" : "border-white/10 bg-white/[0.02] opacity-70")}>
              <div>
                <div className="text-sm font-medium flex items-center gap-2"><Phone className="h-4 w-4 text-rose-400" /> Also SMS residents</div>
                <div className="text-[11px] text-zinc-500">
                  {isCritical ? "Text every resident with a phone on file." : "Available on critical severity only."}
                </div>
              </div>
              <Switch checked={form.send_sms} disabled={!isCritical} onCheckedChange={(v) => setForm({ ...form, send_sms: v })} data-testid="alert-sms-toggle" />
            </div>
            <div className="rounded-md border border-white/10 bg-white/[0.02] p-3 space-y-2">
              <div className="text-xs uppercase tracking-widest text-zinc-500">Dry-run before broadcasting</div>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={testing === "email"}
                  onClick={() => testSend("email")}
                  data-testid="test-email-btn"
                  className="border-white/10"
                >
                  <Mail className="h-4 w-4 mr-1" /> {testing === "email" ? "…" : "Email me"}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={testing === "sms"}
                  onClick={() => testSend("sms")}
                  data-testid="test-sms-btn"
                  className="border-white/10"
                >
                  <Phone className="h-4 w-4 mr-1" /> {testing === "sms" ? "…" : "Text me"}
                </Button>
              </div>
              <div className="text-[10px] text-zinc-500">Sends to your admin email/phone — residents won't see this.</div>
            </div>
            <Button data-testid="alert-send" onClick={submit} disabled={sending} className="w-full bg-orange-500 hover:bg-orange-600">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Broadcast"}
            </Button>
          </div>
        </Card>
        <div className="lg:col-span-2 space-y-3">
          {items.map((a) => {
            const I = IconFor[a.severity] || Info;
            const color = a.severity === "critical" ? "text-rose-400" : a.severity === "warning" ? "text-yellow-400" : "text-indigo-400";
            const emailSent = a.email_sent || 0;
            const emailQueued = a.email_queued || 0;
            const emailTotal = a.email_recipients || 0;
            return (
              <Card key={a.id} className="border-white/10 bg-card p-4" data-testid={`alert-${a.id}`}>
                <div className="flex items-start gap-3">
                  <I className={"h-5 w-5 mt-0.5 " + color} />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-medium">{a.title}</div>
                      <Badge variant="outline" className="border-white/10 capitalize">{a.severity}</Badge>
                      {emailTotal > 0 && (
                        <Badge variant="outline" className={"border-white/10 " + (emailSent > 0 ? "text-emerald-300" : "text-yellow-300")}>
                          {emailSent > 0 ? <CheckCircle2 className="h-3 w-3 mr-1 inline" /> : <Mail className="h-3 w-3 mr-1 inline" />}
                          {emailSent > 0 ? `emailed ${emailSent}/${emailTotal}` : `${emailQueued} queued`}
                        </Badge>
                      )}
                      {(a.sms_recipients || 0) > 0 && (
                        <Badge variant="outline" className={"border-white/10 " + ((a.sms_sent || 0) > 0 ? "text-emerald-300" : "text-yellow-300")}>
                          <Phone className="h-3 w-3 mr-1 inline" />
                          {(a.sms_sent || 0) > 0 ? `SMS ${a.sms_sent}/${a.sms_recipients}` : `${a.sms_queued || 0} queued`}
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-zinc-300 mt-1">{a.body}</p>
                    <div className="text-xs text-zinc-500 mt-1">{new Date(a.created_at).toLocaleString()}</div>
                  </div>
                </div>
              </Card>
            );
          })}
          {items.length === 0 && <div className="text-sm text-muted-foreground">No alerts.</div>}
        </div>
      </div>
    </div>
  );
}
